/**
 * Shared Header Component
 * Provides consistent header functionality across all portals
 */

import React from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, useTheme, alpha } from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  NotificationsOutlined as NotificationsIcon,
  AccountCircleOutlined as AccountIcon,
} from '@mui/icons-material';
import { useLayout } from '../LayoutProvider';
import useThemeMode from '../../../Features/Theme/useThemeMode';

/**
 * Header Component
 * @param {Object} props - Component props
 * @param {Function} props.onMenuClick - Menu button click handler
 * @param {boolean} props.hideMenuButton - Whether to hide menu button
 * @param {Array} props.actions - Custom action buttons
 * @param {string} props.title - Custom header title
 * @param {ReactNode} props.logo - Custom logo component
 * @param {ReactNode} props.userMenu - Custom user menu component
 */
export const Header = ({
  onMenuClick,
  hideMenuButton = false,
  actions = [],
  title,
  logo,
  userMenu,
}) => {
  const { toggleMode } = useThemeMode();
  const theme = useTheme();
  const {
    layoutConfig,
    themeConfig,
    pageTitle,
    headerActions,
    navigationConfig,
    layoutStyles,
    isDark,
    screenSize,
    portalType,
  } = useLayout();

  // Determine if we should show the menu button
  const showMenuButton = !hideMenuButton && navigationConfig.showSideNav;

  // Get header title
  const headerTitle = title || pageTitle || getDefaultTitle(portalType);

  // Combine custom actions with layout actions
  const allActions = [...actions, ...headerActions];

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        height: layoutStyles.main.marginTop || layoutConfig.headerHeight,
        ...layoutStyles.glassMorphism,
        borderBottom: `1px solid ${isDark ? themeConfig.borderDark : themeConfig.borderLight}`,
        color: isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Toolbar
        sx={{
          height: '100%',
          minHeight: `${layoutStyles.main.marginTop || layoutConfig.headerHeight}px !important`,
          px: {
            md: screenSize === 'laptop' ? 2 : 3,
            lg: screenSize === 'desktop' ? 4 : 3,
            xl: screenSize === 'monitor' ? 6 : 4,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Menu Button */}
        {showMenuButton && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={onMenuClick}
            sx={{
              mr: 2,
              color: isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
              '&:hover': {
                backgroundColor: alpha(
                  isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
                  0.08
                ),
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo/Title Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 0 }}>
          {logo && <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{logo}</Box>}

          <Typography
            variant={screenSize === 'laptop' ? 'h6' : screenSize === 'desktop' ? 'h5' : 'h4'}
            component="h1"
            sx={{
              fontWeight: 600,
              color: isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
              background: isDark
                ? `linear-gradient(45deg, ${themeConfig.primaryColorDark}, ${themeConfig.accentColor})`
                : `linear-gradient(45deg, ${themeConfig.primaryColor}, ${themeConfig.accentColor})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              transition: 'all 0.3s ease',
            }}
          >
            {headerTitle}
          </Typography>
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Custom Actions */}
        {allActions.map((action, index) => (
          <Box key={index} sx={{ ml: 1 }}>
            {action}
          </Box>
        ))}

        {/* Default Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          {/* Notifications */}
          <IconButton
            color="inherit"
            aria-label="notifications"
            sx={{
              ml: 1,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            <NotificationsIcon />
          </IconButton>

          {/* Theme Toggle */}
          <IconButton
            color="inherit"
            aria-label="toggle theme"
            onClick={toggleMode}
            sx={{ ml: 1 }}
          >
            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* User Menu */}
          {userMenu ? (
            <Box sx={{ ml: 1 }}>{userMenu}</Box>
          ) : (
            <IconButton color="inherit" aria-label="user account" sx={{ ml: 1 }}>
              <AccountIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

/**
 * Get default title for portal
 * @param {string} portalType - Portal type
 * @returns {string} Default title
 */
const getDefaultTitle = (portalType) => {
  const titles = {
    bureau: 'Bureau Portal',
    admin: 'Admin Dashboard',
    client: 'Client Portal',
    'super-admin': 'Super Admin',
  };

  return titles[portalType] || 'FleetCam';
};

export default Header;
